import {
  CheckCircle,
  Clock,
  Desktop,
  DeviceMobile,
  DeviceTablet,
  DownloadSimple,
  Eye,
  Heart,
  MagnifyingGlass,
  Television,
  Users,
  type Icon,
} from '@/core/ui/icons';
import { useMemo, useState, type ReactNode } from 'react';
import { Button, Card, cn, EmptyState, ErrorState, Skeleton } from '@/core/ui';
import { useAudience } from '../application/audience';
import { DEVICE_LABELS, watchedLabel, type Audience, type Viewer } from '../domain/engagement';
import { audienceCsv, csvFileName } from '../domain/export';

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const DEVICE_ICONS: Record<string, Icon> = {
  desktop: Desktop,
  mobile: DeviceMobile,
  tablet: DeviceTablet,
  tv: Television,
};

type SortKey = 'last_seen_at' | 'percent' | 'watched_s' | 'full_name';
const SORTS: { key: SortKey; label: string }[] = [
  { key: 'last_seen_at', label: 'Mais recentes' },
  { key: 'percent', label: 'Mais assistido (%)' },
  { key: 'watched_s', label: 'Mais tempo' },
  { key: 'full_name', label: 'Nome' },
];

const fold = (s: string) => s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();

interface Props {
  lessonId: string;
  /** Abre o relatório completo da pessoa. */
  onOpenPerson?: (userId: string) => void;
}

/** Audiência de uma aula: números gerais e quem assistiu, exportável em CSV. */
export function AudienceView({ lessonId, onOpenPerson }: Props) {
  const audience = useAudience(lessonId);
  const [term, setTerm] = useState('');
  const [sort, setSort] = useState<SortKey>('last_seen_at');

  const people = useMemo(() => {
    const list = audience.data?.people ?? [];
    const t = fold(term.trim());
    const filtered = t
      ? list.filter((p) =>
          fold([p.full_name, p.email, p.cargo, p.area].filter(Boolean).join(' ')).includes(t),
        )
      : list;
    return [...filtered].sort((a, b) =>
      sort === 'full_name'
        ? (a.full_name || a.email).localeCompare(b.full_name || b.email, 'pt-BR')
        : sort === 'last_seen_at'
          ? b.last_seen_at.localeCompare(a.last_seen_at)
          : b[sort] - a[sort],
    );
  }, [audience.data, term, sort]);

  if (audience.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }
  if (audience.isError) return <ErrorState error={audience.error} onRetry={() => void audience.refetch()} />;

  const a = audience.data;

  return (
    <div className="space-y-4">
      <Summary audience={a} />

      <Card className="overflow-hidden">
        <div className="flex flex-col gap-3 border-b border-line p-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <MagnifyingGlass
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-subtle"
            />
            <input
              type="search"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Buscar por nome, e-mail, cargo ou área"
              aria-label="Buscar pessoas"
              className="h-9 w-full rounded-xl bg-surface-2/60 pr-3 pl-9 text-sm ring-1 ring-transparent placeholder:text-fg-subtle hover:ring-line-strong focus:ring-2 focus:ring-brand-400 focus:outline-none"
            />
          </div>
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value as SortKey)}
            aria-label="Ordenar"
            className="h-9 rounded-xl bg-surface-2/60 px-3 text-sm ring-1 ring-transparent hover:ring-line-strong focus:ring-2 focus:ring-brand-400 focus:outline-none"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </select>
          <Button size="sm" variant="secondary" disabled={a.people.length === 0} onClick={() => download(a)}>
            <DownloadSimple aria-hidden className="size-4" />
            Exportar CSV
          </Button>
        </div>

        {a.people.length === 0 ? (
          <div className="p-6">
            <EmptyState title="Ninguém assistiu esta aula ainda" />
          </div>
        ) : people.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-fg-subtle">Ninguém encontrado com “{term}”.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[56rem] text-sm">
              <thead className="bg-gray-50/60 text-left text-xs font-medium tracking-wide text-gray-500">
                <tr className="border-b border-line">
                  <th className="px-4 py-2.5 font-medium">Pessoa</th>
                  <th className="px-4 py-2.5 font-medium">Progresso</th>
                  <th className="px-4 py-2.5 text-right font-medium">Assistido</th>
                  <th className="px-4 py-2.5 text-right font-medium">Sessões</th>
                  <th className="px-4 py-2.5 font-medium">Último acesso</th>
                  <th className="px-4 py-2.5 font-medium">Dispositivo</th>
                  <th className="px-4 py-2.5 font-medium">IP</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => (
                  <ViewerRow key={p.user_id} viewer={p} onOpen={onOpenPerson} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function Summary({ audience: a }: { audience: Audience }) {
  return (
    <dl className="glass grid grid-cols-2 gap-px overflow-hidden rounded-2xl sm:grid-cols-3 xl:grid-cols-6">
      <Stat icon={Eye} label="Visualizações" value={a.views} />
      <Stat icon={Users} label="Pessoas" value={a.viewers} />
      <Stat
        icon={CheckCircle}
        label="Concluíram"
        value={a.completed}
        hint={`${Math.round(a.completion_rate * 100)}%`}
      />
      <Stat icon={Heart} label="Curtidas" value={a.likes} />
      <Stat icon={Clock} label="Tempo médio" value={watchedLabel(a.avg_watched_s)} />
      <Stat icon={Clock} label="Tempo total" value={watchedLabel(a.total_watched_s)} />
    </dl>
  );
}

function Stat({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: Icon;
  label: string;
  value: ReactNode;
  hint?: string;
}) {
  return (
    <div className="bg-surface/40 p-4">
      <dt className="flex items-center gap-1.5 text-xs text-fg-subtle">
        <Icon aria-hidden className="size-3.5" />
        {label}
      </dt>
      <dd className="mt-1 flex items-baseline gap-2 text-xl font-semibold tabular-nums">
        {value}
        {hint && <span className="text-xs font-normal text-fg-subtle">{hint}</span>}
      </dd>
    </div>
  );
}

function ViewerRow({ viewer: v, onOpen }: { viewer: Viewer; onOpen?: (userId: string) => void }) {
  const Device = DEVICE_ICONS[v.device_type ?? ''] ?? Desktop;
  const name = v.full_name || v.email;
  return (
    <tr
      onClick={onOpen ? () => onOpen(v.user_id) : undefined}
      className={cn('border-b border-line last:border-0', onOpen && 'cursor-pointer hover:bg-surface-2/50')}
    >
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-3">
          <span
            aria-hidden
            className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-400"
          >
            {initials(name)}
          </span>
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 truncate font-medium">
              {onOpen ? (
                <button
                  type="button"
                  className="truncate hover:text-brand-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpen(v.user_id);
                  }}
                >
                  {name}
                </button>
              ) : (
                name
              )}
              {v.liked && (
                <Heart aria-label="Curtiu" weight="fill" className="size-3 shrink-0 text-brand-400" />
              )}
            </p>
            <p className="truncate text-xs text-fg-subtle">
              {[v.cargo, v.area].filter(Boolean).join(' · ') || v.email}
            </p>
          </div>
        </div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-2">
            <span
              className={cn('block h-full', v.completed ? 'bg-brand-400' : 'bg-brand-400')}
              style={{ width: `${Math.min(100, v.percent)}%` }}
            />
          </span>
          <span className="text-xs text-fg-muted tabular-nums">
            {v.completed ? 'Concluiu' : `${v.percent}%`}
          </span>
        </div>
      </td>
      <td className="px-4 py-2.5 text-right text-fg-muted tabular-nums">{watchedLabel(v.watched_s)}</td>
      <td className="px-4 py-2.5 text-right text-fg-muted tabular-nums">{v.sessions}</td>
      <td className="px-4 py-2.5 text-fg-muted tabular-nums">{dateTime.format(new Date(v.last_seen_at))}</td>
      <td className="px-4 py-2.5 text-xs text-fg-muted">
        <span className="inline-flex items-center gap-1.5">
          <Device aria-hidden className="size-4" />
          {DEVICE_LABELS[v.device_type ?? ''] ?? '—'}
          <span className="text-fg-subtle">{[v.browser, v.os].filter(Boolean).join(' · ')}</span>
        </span>
      </td>
      <td className="px-4 py-2.5 font-mono text-xs text-fg-subtle">{v.ip ?? '—'}</td>
    </tr>
  );
}

function download(a: Audience) {
  const blob = new Blob([audienceCsv(a)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csvFileName(a.lesson_title);
  link.click();
  URL.revokeObjectURL(url);
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')).toUpperCase() || '?';
}
